import type { ReactNode } from 'react';
import { Chip } from '@heroui/react/chip';
import { cn } from '@/utils/cn';

/**
 * Status pill — HeroUI's Chip in its `soft` variant.
 * The ONLY way to show state (paid / pending / overdue / …).
 */
export type BadgeTone =
  | 'neutral'
  | 'primary'
  | 'success'
  | 'warning'
  | 'danger'
  | 'info';

/** HeroUI Chip has no `info` colour, so that one tone is themed here. */
const COLOR = {
  neutral: 'default',
  primary: 'accent',
  success: 'success',
  warning: 'warning',
  danger: 'danger',
  info: 'default',
} as const;

export interface BadgeProps {
  tone?: BadgeTone;
  /** Renders a leading dot — useful for live / attention states. */
  withDot?: boolean;
  icon?: ReactNode;
  size?: 'sm' | 'md' | 'lg';
  children: ReactNode;
  className?: string;
  /** Anchor for a guided tour step (see `components/tour`). */
  'data-tour'?: string;
}

export function Badge({
  tone = 'neutral',
  withDot = false,
  icon,
  size = 'sm',
  children,
  className,
  'data-tour': tourAnchor,
}: BadgeProps) {
  return (
    <Chip
      data-tour={tourAnchor}
      variant="soft"
      color={COLOR[tone]}
      size={size}
      className={cn(
        // A badge is a label, not a paragraph: wrapping "Ending soon" onto two
        // lines inside a narrow table column made the chip taller than its row
        // and broke the alignment of every other cell. Never wraps; the column
        // is what should widen.
        'gap-1.5 whitespace-nowrap',
        tone === 'info' && 'bg-info-50 text-info-700',
        className,
      )}
    >
      {withDot && (
        <span
          className="size-1.5 rounded-full bg-current opacity-70"
          aria-hidden
        />
      )}
      {icon}
      {/* Inline row: callers that pass an icon among the children (status
          badges do) had it stacked above the text. */}
      <Chip.Label className="inline-flex items-center gap-1">
        {children}
      </Chip.Label>
    </Chip>
  );
}
